import { createElement } from 'react';
import { createRoot } from 'react-dom/client';
import {
  Heading1,
  Heading2,
  Heading3,
  Quote,
  List,
  ListOrdered,
  ListChecks,
  Code2,
  Table,
  Minus,
  Info,
  ChevronRight,
  ImageIcon,
  Video,
} from 'lucide-react';
import { Extension, type Editor } from '@tiptap/core';
import Suggestion, { type SuggestionProps, type SuggestionKeyDownProps } from '@tiptap/suggestion';
import type { SlashCommandItem } from '../components/SlashCommandMenu';
import type { SlashMenuStore } from '../components/slash-menu-store';
import { VideoEmbedInput } from '../components/VideoEmbedInput';

const ICON_SIZE = 15;

function buildItems(editor: Editor, t: (key: string) => string): SlashCommandItem[] {
  return [
    // Text
    {
      key: 'heading1',
      label: t('slashMenu.commands.heading1'),
      description: t('slashMenu.descriptions.heading1'),
      icon: createElement(Heading1, { size: ICON_SIZE }),
      category: 'text',
      command: () => editor.chain().focus().setHeading({ level: 1 }).run(),
    },
    {
      key: 'heading2',
      label: t('slashMenu.commands.heading2'),
      description: t('slashMenu.descriptions.heading2'),
      icon: createElement(Heading2, { size: ICON_SIZE }),
      category: 'text',
      command: () => editor.chain().focus().setHeading({ level: 2 }).run(),
    },
    {
      key: 'heading3',
      label: t('slashMenu.commands.heading3'),
      description: t('slashMenu.descriptions.heading3'),
      icon: createElement(Heading3, { size: ICON_SIZE }),
      category: 'text',
      command: () => editor.chain().focus().setHeading({ level: 3 }).run(),
    },
    {
      key: 'blockquote',
      label: t('slashMenu.commands.blockquote'),
      description: t('slashMenu.descriptions.blockquote'),
      icon: createElement(Quote, { size: ICON_SIZE }),
      category: 'text',
      command: () => editor.chain().focus().setBlockquote().run(),
    },
    // Lists
    {
      key: 'bulletList',
      label: t('slashMenu.commands.bulletList'),
      description: t('slashMenu.descriptions.bulletList'),
      icon: createElement(List, { size: ICON_SIZE }),
      category: 'lists',
      command: () => editor.chain().focus().toggleBulletList().run(),
    },
    {
      key: 'orderedList',
      label: t('slashMenu.commands.orderedList'),
      description: t('slashMenu.descriptions.orderedList'),
      icon: createElement(ListOrdered, { size: ICON_SIZE }),
      category: 'lists',
      command: () => editor.chain().focus().toggleOrderedList().run(),
    },
    {
      key: 'taskList',
      label: t('slashMenu.commands.taskList'),
      description: t('slashMenu.descriptions.taskList'),
      icon: createElement(ListChecks, { size: ICON_SIZE }),
      category: 'lists',
      command: () => editor.chain().focus().toggleTaskList().run(),
    },
    // Blocks
    {
      key: 'codeBlock',
      label: t('slashMenu.commands.codeBlock'),
      description: t('slashMenu.descriptions.codeBlock'),
      icon: createElement(Code2, { size: ICON_SIZE }),
      category: 'blocks',
      command: () => editor.chain().focus().toggleCodeBlock().run(),
    },
    {
      key: 'table',
      label: t('slashMenu.commands.table'),
      description: t('slashMenu.descriptions.table'),
      icon: createElement(Table, { size: ICON_SIZE }),
      category: 'blocks',
      command: () =>
        editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run(),
    },
    {
      key: 'divider',
      label: t('slashMenu.commands.divider'),
      description: t('slashMenu.descriptions.divider'),
      icon: createElement(Minus, { size: ICON_SIZE }),
      category: 'blocks',
      command: () => editor.chain().focus().setHorizontalRule().run(),
    },
    {
      key: 'callout',
      label: t('slashMenu.commands.callout'),
      description: t('slashMenu.descriptions.callout'),
      icon: createElement(Info, { size: ICON_SIZE }),
      category: 'blocks',
      command: () => editor.commands.insertCallout?.('info'),
    },
    {
      key: 'expandable',
      label: t('slashMenu.commands.expandable'),
      description: t('slashMenu.descriptions.expandable'),
      icon: createElement(ChevronRight, { size: ICON_SIZE }),
      category: 'blocks',
      command: () => editor.commands.insertExpandable?.(),
    },
    // Media
    {
      key: 'image',
      label: t('slashMenu.commands.image'),
      description: t('slashMenu.descriptions.image'),
      icon: createElement(ImageIcon, { size: ICON_SIZE }),
      category: 'media',
      command: () => {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = 'image/*';
        input.onchange = () => {
          const file = input.files?.[0];
          if (!file) return;
          editor.view.dom.dispatchEvent(
            new CustomEvent('rich-text:image-file', { detail: { file }, bubbles: true }),
          );
        };
        input.click();
      },
    },
    {
      key: 'video',
      label: t('slashMenu.commands.video'),
      description: t('slashMenu.descriptions.video'),
      icon: createElement(Video, { size: ICON_SIZE }),
      category: 'media',
      command: () => {
        const container = document.createElement('div');
        container.style.cssText =
          'position:fixed;inset:0;z-index:9999;display:flex;align-items:center;justify-content:center;background:rgba(0,0,0,0.3)';
        document.body.appendChild(container);
        const root = createRoot(container);
        const cleanup = () => {
          root.unmount();
          container.remove();
        };
        root.render(
          createElement(VideoEmbedInput, {
            onInsert: (url) => {
              editor.commands.insertVideoEmbed?.(url);
              cleanup();
            },
            onCancel: cleanup,
          }),
        );
      },
    },
  ];
}

export function buildSlashCommandsExtension(tFn?: (key: string) => string, store?: SlashMenuStore) {
  const t = tFn ?? ((key: string) => key.split('.').pop() ?? key);

  return Extension.create({
    name: 'slashCommands',

    addProseMirrorPlugins() {
      // eslint-disable-next-line @typescript-eslint/no-this-alias
      const ext = this;

      return [
        Suggestion({
          editor: this.editor,
          char: '/',
          allowSpaces: false,

          items: ({ query }: { query: string }) => {
            const items = buildItems(ext.editor, t);
            return items.filter(
              (item) =>
                item.label.toLowerCase().includes(query.toLowerCase()) ||
                item.key.toLowerCase().includes(query.toLowerCase()),
            );
          },

          command: ({
            editor,
            range,
            props,
          }: {
            editor: Editor;
            range: { from: number; to: number };
            props: SlashCommandItem & { rangeToDelete: { from: number; to: number } };
          }) => {
            editor.chain().focus().deleteRange(range).run();
            props.command();
          },

          render: () => {
            // The menu is rendered by SlashMenuHost inside the editor subtree; here we
            // only push state into the shared store. This keeps the menu a descendant of
            // any scrollable Dialog so its own scroll works.
            const pushState = (props: SuggestionProps<SlashCommandItem>) => {
              store?.set({
                open: true,
                query: props.query,
                items: buildItems(props.editor, t),
                getRect: () => props.clientRect?.() ?? null,
                onSelect: (item) => props.command(item),
                onClose: () => props.editor.commands.focus(),
              });
            };

            return {
              onStart: pushState,
              onUpdate: pushState,

              onKeyDown(props: SuggestionKeyDownProps) {
                const { event } = props;
                if (event.key === 'Escape') {
                  store?.set({ open: false });
                  return false;
                }
                return (
                  event.key === 'ArrowUp' || event.key === 'ArrowDown' || event.key === 'Enter'
                );
              },

              onExit() {
                store?.set({ open: false });
              },
            };
          },
        }),
      ];
    },
  });
}
